import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Collapse, styled, keyframes } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CrossIcon, Plus2Icon } from "../../assets/icons";
import { Link } from "react-router-dom";
import { StaggeredText, useTextAnimation, BouncingText } from "./TextAnimations";

const faqData = [
  {
    question: "How do I create an account?",
    answer:
      'You can use Quick Register with your phone number to sign up in about two minutes for instant access to app features, as described under "Quick Register."',
    isOpen: true,
  },
  {
    question: "How do I choose my diet center and plan?",
    answer:
      "From the app, first pick a diet center, then browse the plans offered by that center and choose the one that fits your goals; plans can be customized to your preferences.",
    isOpen: false,
  },
  {
    question: "Can I customize or swap meals in my plan?",
    answer:
      "Yes—on the My Meals screen you can view your plan and easily exchange foods to tailor it to your dietary preferences.",
    isOpen: false,
  },
  {
    question: "Where can I see my plan details, invoices, and health info?",
    answer: "Open My Profile to find your meal plans, invoices, and recorded health conditions in one place.",
    isOpen: false,
  },
  {
    question: "How do delivery days work, and when can my plan start?",
    answer:
      "Your plan has a set number of assigned delivery days and specific delivery days of the week; your start date can't be within the \"locked days\" window (e.g., if locked days = 2, you can't start today or tomorrow).",
    isOpen: false,
  },
  {
    question: "Why can't I make last-minute changes to deliveries?",
    answer:
      "Because of locked days—the kitchen needs time to prepare, so changes to your plan are frozen during the locked period before delivery.",
    isOpen: false,
  },
  {
    question: "How do pauses and cancellations work?",
    answer:
      "You can pause deliveries for a time window or from a date onward, but both pause and restart must respect locked days; cancellations inside locked days aren't allowed, though remaining deliveries can be reduced.",
    isOpen: false,
  },
  {
    question: "Can I save multiple addresses and choose where each delivery goes?",
    answer:
      "Yes—save up to 5 addresses (each with a unique title), set a default, and when you schedule a date for a different address it automatically unschedules the previous one; you can also add extra location notes for your driver.",
    isOpen: false,
  },
  {
    question: "How is pricing calculated and when am I charged?",
    answer:
      "Plans have a daily price and a period (days); total price = days × daily price, and the daily amount is deducted for each delivery day.",
    isOpen: false,
  },
  {
    question: "How do I pay, and is it secure? How can I contact support?",
    answer:
      "Payments are handled through the app's secure payment system, and you can reach the team via the contact info shown (email/website) on the brochure.",
    isOpen: false,
  },
];

// ================== BOX ANIMATION KEYFRAMES ==================

const fadeInUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(40px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

// ================== STYLED COMPONENTS ==================

const AnimatedFAQBox = styled(Box, {
  shouldForwardProp: (prop) => !["delay", "trigger", "isOpen"].includes(prop as string),
})<{
  delay: number;
  trigger: boolean;
  isOpen: boolean;
}>(({ delay, trigger, isOpen }) => ({
  opacity: trigger ? 1 : 0,
  transform: trigger ? "translateY(0) scale(1)" : "translateY(40px) scale(0.9)",
  animation: trigger ? `${fadeInUp} 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}s both` : "none",
  background: "var(--body-bg)",
  borderRadius: "16px",
  border: isOpen ? "1px solid var(--base-200)" : "1px solid var(--base-100)",
  boxShadow: isOpen ? "0px 2px 0px 0px var(--base-200)" : "none",
  width: "100%",
  overflow: "hidden",
  transition: "all 0.3s ease, opacity 0.8s ease, transform 0.8s ease",
  "&:hover": {
    transform: trigger ? "translateY(-2px) scale(1.01)" : "translateY(40px) scale(0.9)",
    backgroundColor: isOpen ? "none" : "var(--base-100)",
    borderColor: isOpen ? "var(--base-200)" : "var(--base-300)",
  },
}));

const AnimatedLeftSection = styled(Box, {
  shouldForwardProp: (prop) => !["trigger"].includes(prop as string),
})<{ trigger: boolean }>(({ trigger }) => ({
  opacity: trigger ? 1 : 0,
  transform: trigger ? "translateX(0)" : "translateX(-30px)",
  transition: "all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.5s",
}));

const FAQSection: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [openFAQ, setOpenFAQ] = useState(0);
  const { elementRef: leftRef, isVisible: leftVisible } = useTextAnimation(0.2);
  const { elementRef: rightRef, isVisible: rightVisible } = useTextAnimation(0.1);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? -1 : index);
  };

  // Use translations for FAQ data
  const translatedFaqData = [
    {
      question: t('common.faq.question1', faqData[0].question),
      answer: t('common.faq.answer1', faqData[0].answer),
      isOpen: true,
    },
    {
      question: t('common.faq.question2', faqData[1].question),
      answer: t('common.faq.answer2', faqData[1].answer),
      isOpen: false,
    },
    {
      question: t('common.faq.question3', faqData[2].question),
      answer: t('common.faq.answer3', faqData[2].answer),
      isOpen: false,
    },
    {
      question: t('common.faq.question4', faqData[3].question),
      answer: t('common.faq.answer4', faqData[3].answer),
      isOpen: false,
    },
    {
      question: t('common.faq.question5', faqData[4].question),
      answer: t('common.faq.answer5', faqData[4].answer),
      isOpen: false,
    },
    {
      question: t('common.faq.question6', faqData[5].question),
      answer: t('common.faq.answer6', faqData[5].answer),
      isOpen: false,
    },
    {
      question: t('common.faq.question7', faqData[6].question),
      answer: t('common.faq.answer7', faqData[6].answer),
      isOpen: false,
    },
    {
      question: t('common.faq.question8', faqData[7].question),
      answer: t('common.faq.answer8', faqData[7].answer),
      isOpen: false,
    },
    {
      question: t('common.faq.question9', faqData[8].question),
      answer: t('common.faq.answer9', faqData[8].answer),
      isOpen: false,
    },
    {
      question: t('common.faq.question10', faqData[9].question),
      answer: t('common.faq.answer10', faqData[9].answer),
      isOpen: false,
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: { xs: "32px", sm: "40px", md: "60px", lg: "80px" },
        width: "100%",
        maxWidth: "1110px",
        margin: "0 auto",
        px: { xs: 4, sm: 3, md: 4, lg: 0 },
        py: { xs: 6, sm: 6, md: 32 },
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      {/* Left Content */}
      <AnimatedLeftSection
        ref={leftRef}
        trigger={leftVisible}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: "24px", sm: "32px", md: "64px" },
          alignItems: "flex-start",
          justifyContent: "flex-start",
          width: { xs: "100%", md: "544px" },
          flex: "none",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: "8px", sm: "12px" },
            alignItems: "flex-start",
            justifyContent: "flex-start",
            width: "100%",
            color: "var(--font-dark)",
          }}
        >
          <StaggeredText
            text={t('common.faq.title')}
            staggerBy="words"
            staggerDelay={0.1}
            delay={0.2}
            trigger={leftVisible}
            component={Typography}
            sx={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 600,
              fontSize: { xs: "20px", sm: "22px", md: "24px", lg: "28px" },
              lineHeight: 1.6,
              color: "var(--font-dark)",
            }}
          />
          <StaggeredText
            text={t('common.faq.description')}
            staggerBy="words"
            staggerDelay={0.05}
            delay={0.8}
            trigger={leftVisible}
            component={Typography}
            sx={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              fontSize: { xs: "14px", sm: "15px", md: "16px", lg: "16px" },
              lineHeight: 1.5,
              color: "var(--font-dark)",
            }}
          />
        </Box>

        {/* Contact CTA */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: "12px", sm: "16px" },
            alignItems: "flex-start",
            justifyContent: "flex-start",
            px: { xs: 2.5, sm: 3, md: 2.5 },
            py: 0,
            width: "100%",
            borderLeft: { xs: "none", md: "1.5px solid var(--secondary-default)" },
            borderTop: { xs: "1.5px solid var(--secondary-default)", md: "none" },
            pt: { xs: 2.5, md: 0 },
            pl: { xs: 0, md: 2.5 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              width: "100%",
            }}
          >
            <StaggeredText
              text={t('common.faq.cantFindTitle')}
              staggerBy="words"
              staggerDelay={0.08}
              delay={1.4}
              trigger={leftVisible}
              component={Typography}
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 600,
                fontSize: { xs: "13px", sm: "14px" },
                lineHeight: 1.6,
                color: "var(--font-default)",
              }}
            />
            <StaggeredText
              text={t('common.faq.cantFindDescription')}
              staggerBy="words"
              staggerDelay={0.06}
              delay={1.8}
              trigger={leftVisible}
              component={Typography}
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 400,
                fontSize: { xs: "11px", sm: "12px" },
                lineHeight: "20px",
                color: "var(--font-light)",
                maxWidth: { xs: "100%", md: "506px" },
              }}
            />
          </Box>
          <Link to="/contact">
            <BouncingText
              delay={2.4}
              trigger={leftVisible}
              component={Button}
              sx={{
                background: "var(--secondary-lighter)",
                color: "var(--secondary-default)",
                px: 2,
                py: 1,
                borderRadius: "8px",
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: { xs: "13px", sm: "14px" },
                textTransform: "none",
                border: "none",
                boxShadow: "none",
                "&:hover": {
                  background: "#fef0e0",
                  boxShadow: "none",
                },
              }}
            >
              {t('common.faq.contactButton')}
            </BouncingText>
          </Link>
        </Box>
      </AnimatedLeftSection>

      {/* Right FAQ List */}
      <Box
        ref={rightRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: "8px", sm: "12px" },
          alignItems: "flex-start",
          justifyContent: "flex-start",
          width: { xs: "100%", md: "531px" },
          flex: "none",
        }}
      >
        {translatedFaqData.map((faq, index) => {
          const isOpen = openFAQ === index;
          return (
            <AnimatedFAQBox key={index} delay={0.2 + index * 0.15} trigger={rightVisible} isOpen={isOpen}>
              {/* Question Header */}
              <Box
                onClick={() => toggleFAQ(index)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: { xs: 3, sm: 4, md: 6 },
                  py: { xs: 2.5, sm: 3, md: 5 },
                  minHeight: "56px",
                  cursor: "pointer",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: isOpen ? 600 : 500,
                    fontSize: { xs: "14px", sm: "15px", md: "16px" },
                    lineHeight: "26px",
                    color: isOpen ? "var(--font-darkest)" : "var(--font-dark)",
                    flex: 1,
                    pr: 2,
                    transition: "all 0.2s ease",
                  }}
                >
                  {faq.question}
                </Typography>
                <Box
                  sx={{
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s ease",
                    color: isOpen ? "var(--font-darkest)" : "var(--font-dark)",
                  }}
                >
                  {isOpen ? (
                    <CrossIcon style={{ width: "20px", height: "20px" }} />
                  ) : (
                    <Plus2Icon style={{ width: "20px", height: "20px" }} />
                  )}
                </Box>
              </Box>

              {/* Answer Content with smooth collapse animation */}
              <Collapse
                in={isOpen}
                timeout={300}
                sx={{
                  "& .MuiCollapse-wrapper": {
                    width: "100%",
                  },
                }}
              >
                <Box
                  sx={{
                    px: { xs: 3, sm: 4, md: 6 },
                    pb: { xs: 2.5, sm: 3, md: 5 },
                    pt: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 400,
                      fontSize: { xs: "13px", sm: "14px" },
                      lineHeight: "24px",
                      color: "var(--font-dark)",
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </Box>
              </Collapse>
            </AnimatedFAQBox>
          );
        })}
      </Box>
    </Box>
  );
};

export default FAQSection;
